import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';

export interface EvolutionGrupo {
  id: string | null;
  nome: string;
  descricao: string | null;
  foto_url: string | null;
  total_participantes: number;
  participantes: string[];
}

export interface EvolutionInstanceInfo {
  reference: string;
  name: string;
  status: string;
  raw_status: string | null;
  owner_jid?: string | null;
  profile_name?: string | null;
  profile_picture?: string | null;
  token?: string | null;
  updated_at?: string | null;
}

interface EvolutionInstanceOverviewResponse {
  success: boolean;
  data: {
    instance: EvolutionInstanceInfo;
    grupos: EvolutionGrupo[];
    total: number;
  };
}

interface EvolutionActionResponse {
  success: boolean;
  data: {
    message: string;
    instance: EvolutionInstanceInfo;
    qr?: string | null;
    pairingCode?: string | null;
  };
}

export interface CategoriaSistema {
  id: number;
  nome: string;
  descricao: string | null;
  tipo: 'licitacao' | 'noticia' | 'ambos';
  ativo: boolean;
}

export interface GrupoCategoria {
  id: number;
  nome: string;
  descricao: string | null;
  tipo: string;
  peso: number;
}

export interface GrupoDB {
  id: number;
  nome: string;
  descricao: string | null;
  tipo: string;
  ativo: boolean;
  uf: string[] | null;
  palavras_chave: string[] | null;
  palavras_chave_exclusao: string[] | null;
  nichos: string[] | null;
  cnaes: string[] | null;
  recebe_licitacoes: boolean;
  recebe_noticias: boolean;
  correlacao_automatica: boolean;
  score_minimo: number;
  total_membros: number;
  created_at: string;
  updated_at: string;
  categorias?: GrupoCategoria[];
}

interface GruposListResponse {
  success: boolean;
  data: {
    grupos: GrupoDB[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

interface GrupoResponse {
  success: boolean;
  data: {
    grupo: GrupoDB;
  };
}

interface CategoriasSistemaResponse {
  success: boolean;
  data: {
    categorias: CategoriaSistema[];
  };
}

interface GrupoCategoriasResponse {
  success: boolean;
  data: {
    grupoId: number;
    categorias: GrupoCategoria[];
  };
}

// Evolution hooks
export function useEvolutionInstanceOverview() {
  return useQuery({
    queryKey: ['evolution-instance-overview'],
    queryFn: async () => {
      const response = await api.get<EvolutionInstanceOverviewResponse>('/grupos/evolution/instance');
      return response.data;
    }
  });
}

export function useAtualizarEvolutionInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post<EvolutionActionResponse>('/grupos/evolution/instance/atualizar');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evolution-instance-overview'] });
    }
  });
}

export function useConectarEvolutionInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post<EvolutionActionResponse>('/grupos/evolution/instance/conectar');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evolution-instance-overview'] });
    }
  });
}

export function useReiniciarEvolutionInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post<EvolutionActionResponse>('/grupos/evolution/instance/reiniciar');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evolution-instance-overview'] });
    }
  });
}

export function useQrCodeEvolutionInstance() {
  return useMutation({
    mutationFn: async () => {
      const response = await api.get<EvolutionActionResponse>('/grupos/evolution/instance/qrcode');
      return response.data;
    }
  });
}

// Database grupos hooks
export function useGruposDB(page = 1, limit = 50) {
  return useQuery({
    queryKey: ['grupos-db', page, limit],
    queryFn: async () => {
      const response = await api.get<GruposListResponse>(`/grupos?page=${page}&limit=${limit}`);
      return response.data;
    }
  });
}

export function useGrupoDB(id: number | null) {
  return useQuery({
    queryKey: ['grupo-db', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await api.get<GrupoResponse>(`/grupos/${id}`);
      return response.data;
    },
    enabled: !!id
  });
}

export function useCreateGrupoDB() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<GrupoDB>) => {
      const response = await api.post<GrupoResponse>('/grupos', { ...data, tipo: 'whatsapp' });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grupos-db'] });
    }
  });
}

export function useUpdateGrupoDB() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<GrupoDB> }) => {
      const response = await api.put<GrupoResponse>(`/grupos/${id}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['grupos-db'] });
      queryClient.invalidateQueries({ queryKey: ['grupo-db', variables.id] });
    }
  });
}

export function useCategoriasSistema(tipo?: string) {
  return useQuery({
    queryKey: ['categorias-sistema', tipo],
    queryFn: async () => {
      const url = tipo ? `/grupos/categorias/sistema?tipo=${tipo}` : '/grupos/categorias/sistema';
      const response = await api.get<CategoriasSistemaResponse>(url);
      return response.data;
    }
  });
}

export function useGrupoCategorias(grupoId: number | null) {
  return useQuery({
    queryKey: ['grupo-categorias', grupoId],
    queryFn: async () => {
      if (!grupoId) return null;
      const response = await api.get<GrupoCategoriasResponse>(`/grupos/${grupoId}/categorias`);
      return response.data;
    },
    enabled: !!grupoId
  });
}

export function useAddGrupoCategoria() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ grupoId, categoriaId, peso }: { grupoId: number; categoriaId: number; peso?: number }) => {
      const response = await api.post<{ success: boolean; data: { grupo_categoria: { id: number } } }>(`/grupos/${grupoId}/categorias`, { categoria_id: categoriaId, peso });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['grupo-categorias', variables.grupoId] });
      queryClient.invalidateQueries({ queryKey: ['grupo-db', variables.grupoId] });
    }
  });
}

export function useRemoveGrupoCategoria() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ grupoId, categoriaId }: { grupoId: number; categoriaId: number }) => {
      const response = await api.delete<{ success: boolean; data: { message: string } }>(`/grupos/${grupoId}/categorias/${categoriaId}`);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['grupo-categorias', variables.grupoId] });
      queryClient.invalidateQueries({ queryKey: ['grupo-db', variables.grupoId] });
    }
  });
}
